import bannerPlaceholder from "assets/images/evento-banner.jpg";
import WeddingRose from "templates/WeddingRose";
import WeddingWhite from "templates/WeddingWhite";
import Birthday from "templates/Birthday";
import { useEventStore, useGuestStore, useFeedStore } from "store";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useMemo } from "react";
import { useLogEvent, getBrowser } from "hooks/useLogEvent";
import { useIntl } from "react-intl";
import { shareLink } from "utils/utils";
import { clarity } from "react-microsoft-clarity";
import { setEventTimingTag, setTimeOfDayTag } from "utils/clarityHelpers";

export default function Home() {
  const { event, language } = useEventStore();
  const { guest, clearStore: clearGuestStore } = useGuestStore();
  const { clearFeedCache, isCacheValid: isFeedCacheValid } = useFeedStore();
  const { logHome, logShareButton } = useLogEvent();
  const [searchParams, setSearchParams] = useSearchParams();

  const redirectTo = searchParams.get("redirectTo");
  const scanLocation = searchParams.get("scanLocation");
  const navigate = useNavigate();

  const handleInvalidRedirect = useCallback(
    (decodedRedirectTo: string) => {
      console.warn(
        `Route ${decodedRedirectTo} is not allowed for this event, removing redirectTo parameter`
      );
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("redirectTo");
      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const isRouteAllowed = useCallback(
    (path: string) => {
      const feature = path.replace("/", "").split("/")[1];
      switch (feature) {
        case "photo-challenge":
          if (event?.betaFeatures?.includes("photo_challenge")) {
            return true;
          }
          handleInvalidRedirect(path);
          break;
        case "quiz":
          if (event?.betaFeatures?.includes("quiz")) {
            return true;
          }
          handleInvalidRedirect(path);
          break;
        case "team-match":
          if (event?.betaFeatures?.includes("team_match")) {
            return true;
          }
          handleInvalidRedirect(path);
          break;
        case "bingo":
          if (event?.betaFeatures?.includes("bingo")) {
            return true;
          }
          handleInvalidRedirect(path);
          break;
        default:
          handleInvalidRedirect(path);
          return false;
      }
    },
    [event?.betaFeatures, handleInvalidRedirect]
  );

  useEffect(() => {
    if (redirectTo && isRouteAllowed(redirectTo)) {
      navigate(redirectTo);
    }
  }, [redirectTo, isRouteAllowed, handleInvalidRedirect, navigate]);

  const qrCodeLink = useMemo(() => {
    return `https://evento.capsuladotempo.com/?code=${event?.code}`;
  }, [event?.code]);

  const intl = useIntl();

  const onShare = useCallback(() => {
    const text = intl.formatMessage({
      id: `message.shareLink`,
      defaultMessage: "Compartilhe seus momentos favoritos da festa!",
    });

    shareLink(qrCodeLink, text, logShareButton);
  }, [qrCodeLink, intl, logShareButton]);

  const onCapsulaLandingPageClick = useCallback(() => {
    const capsulaLadingPage = "https://videorecados.capsuladotempo.com";

    window.open(capsulaLadingPage, "_blank");
  }, []);

  const convertFontDefault = useCallback((font: string) => {
    if (font === "default") {
      return null;
    }
    return font;
  }, []);

  const actionButtonLabel = useMemo(() => {
    return intl.formatMessage({
      id: `button.home.sendFiles`,
      defaultMessage: "Enviar arquivos",
    });
  }, [intl]);

  const onClick = useCallback(() => {
    logHome();

    if (
      !guest &&
      event?.betaFeatures !== undefined &&
      event?.betaFeatures?.length !== 0
    ) {
      return navigate("/guest-form");
    }
    if (guest && event?.betaFeatures?.includes("feed")) {
      return navigate("/feed/v2");
    }
    navigate("/options");
  }, [logHome, navigate, event, guest]);

  useEffect(() => {
    // Set Clarity tags for user context
    if (clarity.hasStarted() && event) {
      clarity.setTag("event_code", event.code);
      clarity.setTag("device_type", getBrowser().device);
      clarity.setTag("release_mode", event.uploadsReleaseMode || "unknown");
      clarity.setTag("language", language);
      clarity.setTag("template", event.customization?.template?.name || "wedding-rose");

      // Track user type (anonymous vs joined)
      if (guest?.id) {
        clarity.setTag("guest_id", guest.id);
        clarity.setTag("user_type", "joined");
      } else {
        clarity.setTag("user_type", "anonymous");
      }

      // Track connection quality if available
      const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
      if (connection && connection.effectiveType) {
        clarity.setTag("connection_quality", connection.effectiveType);
      }

      // Track QR code location if available
      if (scanLocation) {
        clarity.setTag("qrcode_location", scanLocation);
      }

      // Track redirect destination if available
      if (redirectTo) {
        clarity.setTag("redirect_to", redirectTo);
      }

      // Track available beta features
      if (event.betaFeatures && event.betaFeatures.length > 0) {
        event.betaFeatures.forEach((feature) => {
          clarity.setTag(`feature_${feature}`, "available");
        });
      }

      // Track event timing phase
      if (event.date) {
        setEventTimingTag(event.date);
      }

      // Track time of day
      setTimeOfDayTag();
    }

    // Clear guest store if the event is different
    if (guest && event?.code && guest.eventCode !== event?.code) {
      if (clarity.hasStarted()) {
        clarity.setTag("guest_store_cleared", "true");
      }
      clearGuestStore();
    }
    // Clear feed cache if the event is different or cache is invalid
    if (!event?.code) {
      return;
    }
    if (!isFeedCacheValid(event?.code) || guest?.eventCode !== event?.code) {
      clearFeedCache();
    }
  }, [
    event,
    guest,
    clearGuestStore,
    clearFeedCache,
    isFeedCacheValid,
    scanLocation,
    redirectTo,
    language,
  ]);

  switch (event?.customization?.template?.name) {
    case "wedding-rose":
      const weddingRoseValues = {
        bannerUrl: event.bannerImage || bannerPlaceholder,
        titleFontFamily:
          convertFontDefault(event.customization.template.titleFontFamily) ||
          "font-high-spirited",
        titleFontSize: event.customization.template.titleFontSize || "53px",
        titleOneText: event.customization.template.titleOneText as string,
        titleTwoText: event.customization.template.titleTwoText as string,
        subtitleText: event.customization.template.subtitleText as string,
        onShare: onShare,
        onCapsulaLandingPageClick,
        actionButtonLabel,
        onClick: onClick,
      };

      return <WeddingRose {...weddingRoseValues} />;
    case "wedding-white":
      const weddingWhiteValues = {
        bannerUrl: event.bannerImage || bannerPlaceholder,
        titleFontFamily:
          convertFontDefault(event.customization.template.titleFontFamily) ||
          "font-playfair-display",
        titleFontSize: event.customization.template.titleFontSize || "30px",
        titleOneText: event.customization.template.titleOneText as string,
        titleTwoText: event.customization.template.titleTwoText as string,
        subtitleText: event.customization.template.subtitleText as string,
        onShare: onShare,
        onCapsulaLandingPageClick,
        actionButtonLabel,
        onClick: onClick,
      };

      return <WeddingWhite {...weddingWhiteValues} />;
    case "birthday-pink":
      const birthdayPinkValues = {
        bannerUrl: event.bannerImage || bannerPlaceholder,
        titleFontFamily:
          convertFontDefault(event.customization.template.titleFontFamily) ||
          "font-style-script",
        titleFontSize: event.customization.template.titleFontSize || "70px",
        titleOneText: event.customization.template.titleOneText as string,
        titleTwoText: event.customization.template.titleTwoText as string,
        subtitleText: event.customization.template.subtitleText as string,
        titleOneShadowClass: "before:text-template-pink",
        templateColor: "bg-template-pink",
        wrapperClass: "bg-template-bg-pink",
        onShare: onShare,
        onCapsulaLandingPageClick,
        actionButtonLabel,
        onClick: onClick,
      };

      return <Birthday {...birthdayPinkValues} />;
    case "birthday-blue":
      const birthdayBlueValues = {
        bannerUrl: event.bannerImage || bannerPlaceholder,
        titleFontFamily:
          convertFontDefault(event.customization.template.titleFontFamily) ||
          "font-style-script",
        titleFontSize: event.customization.template.titleFontSize || "70px",
        titleOneText: event.customization.template.titleOneText as string,
        titleTwoText: event.customization.template.titleTwoText as string,
        subtitleText: event.customization.template.subtitleText as string,
        titleOneShadowClass: "before:text-template-blue",
        templateColor: "bg-template-blue",
        wrapperClass: "bg-template-bg-blue",
        onShare: onShare,
        onCapsulaLandingPageClick,
        actionButtonLabel,
        onClick: onClick,
      };

      return <Birthday {...birthdayBlueValues} />;
    case "birthday-yellow":
      const birthdayYellowValues = {
        bannerUrl: event.bannerImage || bannerPlaceholder,
        titleFontFamily:
          convertFontDefault(event.customization.template.titleFontFamily) ||
          "font-style-script",
        titleFontSize: event.customization.template.titleFontSize || "70px",
        titleOneText: event.customization.template.titleOneText as string,
        titleTwoText: event.customization.template.titleTwoText as string,
        subtitleText: event.customization.template.subtitleText as string,
        titleOneShadowClass: "before:text-template-yellow",
        templateColor: "bg-template-yellow",
        wrapperClass: "bg-template-bg-yellow",
        onShare: onShare,
        onCapsulaLandingPageClick,
        actionButtonLabel,
        onClick: onClick,
      };

      return <Birthday {...birthdayYellowValues} />;
    case "birthday-green":
      const birthdayGreenValues = {
        bannerUrl: event.bannerImage || bannerPlaceholder,
        titleFontFamily:
          convertFontDefault(event.customization.template.titleFontFamily) ||
          "font-style-script",
        titleFontSize: event.customization.template.titleFontSize || "70px",
        titleOneText: event.customization.template.titleOneText as string,
        titleTwoText: event.customization.template.titleTwoText as string,
        subtitleText: event.customization.template.subtitleText as string,
        titleOneShadowClass: "before:text-template-green",
        templateColor: "bg-template-green",
        wrapperClass: "bg-template-bg-green",
        onShare: onShare,
        onCapsulaLandingPageClick,
        actionButtonLabel,
        onClick: onClick,
      };

      return <Birthday {...birthdayGreenValues} />;
    case "birthday-purple":
      const birthdayPurpleValues = {
        bannerUrl: event.bannerImage || bannerPlaceholder,
        titleFontFamily:
          convertFontDefault(event.customization.template.titleFontFamily) ||
          "font-style-script",
        titleFontSize: event.customization.template.titleFontSize || "70px",
        titleOneText: event.customization.template.titleOneText as string,
        titleTwoText: event.customization.template.titleTwoText as string,
        subtitleText: event.customization.template.subtitleText as string,
        titleOneShadowClass: "before:text-template-purple",
        templateColor: "bg-template-purple",
        wrapperClass: "bg-template-bg-purple",
        onShare: onShare,
        onCapsulaLandingPageClick,
        actionButtonLabel,
        onClick: onClick,
      };

      return <Birthday {...birthdayPurpleValues} />;
    case "birthday-white":
      const birthdayWhiteValues = {
        bannerUrl: event.bannerImage || bannerPlaceholder,
        titleFontFamily:
          convertFontDefault(event.customization.template.titleFontFamily) ||
          "font-style-script",
        titleFontSize: event.customization.template.titleFontSize || "70px",
        titleOneText: event.customization.template.titleOneText as string,
        titleTwoText: event.customization.template.titleTwoText as string,
        subtitleText: event.customization.template.subtitleText as string,
        titleOneShadowClass: "before:text-template-white",
        templateColor: "bg-black",
        wrapperClass: "bg-white",
        buttonTextColor: "#FFFFFF",
        onShare,
        onCapsulaLandingPageClick,
        actionButtonLabel,
        onClick,
      };

      return <Birthday {...birthdayWhiteValues} />;
    default:
      const defaultValues = {
        bannerUrl: event?.bannerImage || bannerPlaceholder,
        titleFontFamily: event?.customization?.template
          ?.titleFontFamily as string,
        titleFontSize: event?.customization?.template?.titleFontSize || "53px",
        titleOneText: event?.customization?.template?.titleOneText as string,
        subtitleText: event?.customization?.template?.subtitleText as string,
        onShare: onShare,
        onCapsulaLandingPageClick,
        actionButtonLabel,
        onClick: onClick,
      };
      return <WeddingRose {...defaultValues} />;
  }
}
